import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Settings2, Save, ChevronDown, LayoutGrid } from 'lucide-react';
import { tablesAPI } from '../../services/api';
import { toast } from 'sonner';

// Admin dialog to rename the game tables
export const TablesManager = ({ tables, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [names, setNames] = useState({});
  const [saving, setSaving] = useState(false);

  const handleOpen = (isOpen) => {
    if (isOpen) setNames(Object.fromEntries(tables.map((t) => [t.table_id, t.name])));
    setOpen(isOpen);
  };

  const handleSave = async () => {
    if (Object.values(names).some((n) => !n.trim())) {
      toast.error('Chaque table doit avoir un nom');
      return;
    }
    setSaving(true);
    try {
      const updated = await tablesAPI.rename(tables.map((t) => ({ table_id: t.table_id, name: names[t.table_id].trim() })));
      onUpdated(updated);
      toast.success('Noms des tables mis à jour');
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="manage-tables-button" className="border-purple-500/40 text-purple-300 hover:bg-purple-900/40">
          <Settings2 className="mr-2 h-4 w-4" /> Gérer les tables
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-2 border-purple-500/50 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-purple-300 flex items-center gap-2"><LayoutGrid className="h-5 w-5" /> Noms des tables</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {tables.map((t) => (
            <div key={t.table_id} className="space-y-1">
              <Label htmlFor={`table-name-${t.table_id}`} className="text-xs text-gray-400">{t.table_id === 'table_ado' ? 'Table ado' : `Table adulte ${t.table_id.split('_')[1]}`}</Label>
              <Input id={`table-name-${t.table_id}`} data-testid={`table-name-input-${t.table_id}`} maxLength={40}
                value={names[t.table_id] || ''} onChange={(e) => setNames({ ...names, [t.table_id]: e.target.value })}
                className="bg-black/50 border-purple-500/30 text-white" />
            </div>
          ))}
          <Button onClick={handleSave} disabled={saving} data-testid="save-tables-button" className="w-full bg-purple-600 hover:bg-purple-500 text-white">
            <Save className="mr-2 h-4 w-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Multi-select dropdown to assign a member to tables (admin only)
export const MemberTablesSelect = ({ member, tables, onChange }) => {
  const selected = member.tables || [];
  const label = selected.length === 0
    ? 'Aucune table'
    : tables.filter((t) => selected.includes(t.table_id)).map((t) => t.name).join(', ');

  const toggle = (tableId, checked) => {
    const next = checked ? [...selected, tableId] : selected.filter((id) => id !== tableId);
    onChange(member.user_id, next);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={`member-tables-trigger-${member.user_id}`}
          className="flex h-10 w-[150px] sm:w-[180px] items-center justify-between rounded-md border border-purple-500/30 bg-black/50 px-3 py-2 text-sm text-purple-300 hover:border-purple-400/50 transition-colors">
          <span className="truncate">{label}</span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-1" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-black border-purple-500/30 w-[200px]" align="start">
        <DropdownMenuLabel className="text-purple-400 text-xs">Tables du membre</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-purple-500/20" />
        {tables.map((t) => (
          <DropdownMenuCheckboxItem key={t.table_id} data-testid={`member-table-option-${member.user_id}-${t.table_id}`}
            checked={selected.includes(t.table_id)} onCheckedChange={(checked) => toggle(t.table_id, checked)}
            onSelect={(e) => e.preventDefault()} className="text-purple-200 focus:bg-purple-900/50 focus:text-purple-100">
            {t.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
